/**
 * Lytta Theme — Main JavaScript
 * Vanilla JS + Web Components architecture
 */

'use strict';

/* ============================================================
   LYTTA BRAND SIGNATURE (console)
   ============================================================ */
(function() {
  try {
    var title = '%cLYTTA';
    var titleStyle = [
      'font-size: 56px',
      'font-weight: 900',
      'letter-spacing: 0.15em',
      'padding: 16px 0',
      'background: linear-gradient(135deg, #111 0%, #555 100%)',
      '-webkit-background-clip: text',
      'background-clip: text',
      'color: transparent',
      'line-height: 1'
    ].join(';');
    var subtitle = '%cCustom Shopify theme crafted with care by Lytta.';
    var subtitleStyle = 'font-size: 13px; font-weight: 500; color: #111; padding-top: 4px;';
    var cta = '%cWant a theme like this for your brand?\n→ https://lytta.it  ·  info@lytta.it';
    var ctaStyle = 'font-size: 12px; color: #666; padding: 8px 0 16px;';
    console.log(title + '\n' + subtitle + '\n' + cta, titleStyle, subtitleStyle, ctaStyle);
  } catch (e) { /* no-op: some browsers (older Safari) may throw on complex styles */ }
})();

/* ============================================================
   UTILITY FUNCTIONS
   ============================================================ */
const lytta = window.lytta || {};

/**
 * Debounce a function call
 */
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Trap focus inside an element (for modals and drawers)
 */
function trapFocus(element) {
  const focusable = element.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  element.addEventListener('keydown', handleKeydown);
  firstFocusable?.focus();

  return () => element.removeEventListener('keydown', handleKeydown);
}

/**
 * Format money with shop money format. Supports all Shopify placeholders:
 *   {{amount}}                                  → 1234.56
 *   {{amount_no_decimals}}                      → 1234
 *   {{amount_with_comma_separator}}             → 1.234,56
 *   {{amount_no_decimals_with_comma_separator}} → 1.234
 *   {{amount_with_apostrophe_separator}}        → 1'234.56
 *   {{amount_with_space_separator}}             → 1 234.56
 *   {{amount_no_decimals_with_space_separator}} → 1 234
 */
function formatMoney(cents) {
  const fmt = (lytta.settings && lytta.settings.moneyFormat) || '${{amount}}';
  const value = parseFloat(cents) / 100;

  function withDelimiters(num, precision, thousands, decimal) {
    if (isNaN(num) || num == null) return '0';
    const n = num.toFixed(precision);
    const parts = n.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
    switch (placeholder) {
      case 'amount': return withDelimiters(value, 2, ',', '.');
      case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
      case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', '.');
      case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', '.');
      default: return withDelimiters(value, 2, ',', '.');
    }
  });
}

/**
 * Fetch JSON from URL
 */
async function fetchJSON(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/* ============================================================
   OVERLAY MANAGER
   ============================================================ */
const overlay = {
  el: document.getElementById('LyttaOverlay'),
  activeCount: 0,

  show(onClose) {
    this.activeCount++;
    this.el?.classList.add('is-visible');
    this.el?.removeAttribute('aria-hidden');
    this._onClose = onClose;
    this.el?.addEventListener('click', this._handleClick.bind(this), { once: true });
  },

  hide() {
    this.activeCount = Math.max(0, this.activeCount - 1);
    if (this.activeCount === 0) {
      this.el?.classList.remove('is-visible');
      this.el?.setAttribute('aria-hidden', 'true');
    }
  },

  _handleClick() {
    this._onClose?.();
  }
};

/* ============================================================
   SCROLL ANIMATIONS
   ============================================================ */
function initAnimations(root = document) {
  if (!lytta.settings?.animationsEnabled) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // With reduced motion, force visibility immediately so reloaded sections
    // in the theme editor don't get stuck in their invisible opacity:0 state.
    root.querySelectorAll('[data-lytta-animate]:not(.is-visible)').forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  // Skip elements already visible (covers re-init on shopify:section:load).
  root.querySelectorAll('[data-lytta-animate]:not(.is-visible)').forEach(el => observer.observe(el));
}

/* ============================================================
   SPLIT-TEXT REVEAL
   ============================================================ */
function initSplitText() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Auto-tag common section headings when the global flag is on.
  // Explicit [data-lytta-split] attributes are preserved and take precedence.
  if (lytta.settings?.splitTextEnabled) {
    const mode = lytta.settings.splitTextMode === 'chars' ? 'chars' : 'words';
    const autoSelector = [
      '.section-title',
      '.slideshow__heading',
      '.text-image__heading',
      '.video-bg__heading',
      '.collection-page__title'
    ].join(', ');

    document.querySelectorAll(autoSelector).forEach(el => {
      if (!el.hasAttribute('data-lytta-split')) {
        el.setAttribute('data-lytta-split', mode);
      }
    });
  }

  const elements = document.querySelectorAll('[data-lytta-split]');
  if (elements.length === 0) return;

  const splitObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        splitObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

  elements.forEach(el => {
    if (el.dataset.lyttaSplitProcessed === 'true') return;
    const mode = el.dataset.lyttaSplit; // 'words' | 'chars' | 'lines'
    _splitInto(el, mode);
    el.dataset.lyttaSplitProcessed = 'true';
    splitObserver.observe(el);
  });
}

function _splitInto(el, mode) {
  const text = el.textContent.trim();
  // Keep original for a11y
  if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', text);

  let index = 0;
  let html = '';

  if (mode === 'chars') {
    // Keep whitespace between words; wrap each non-space char.
    const words = text.split(/(\s+)/);
    words.forEach(segment => {
      if (/^\s+$/.test(segment)) {
        html += segment;
      } else {
        html += '<span class="split-word">';
        for (const ch of segment) {
          html += `<span class="split-char" style="--split-index:${index}" aria-hidden="true">${_escapeHTML(ch)}</span>`;
          index++;
        }
        html += '</span>';
      }
    });
  } else {
    // Default: words
    const words = text.split(/(\s+)/);
    words.forEach(segment => {
      if (/^\s+$/.test(segment)) {
        html += segment;
      } else {
        html += `<span class="split-word" style="--split-index:${index}" aria-hidden="true">${_escapeHTML(segment)}</span>`;
        index++;
      }
    });
  }

  el.innerHTML = html;
}

function _escapeHTML(str) {
  return str.replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));
}

/* ============================================================
   CART — FETCH AND UPDATE
   ============================================================ */
const cart = {
  async add(items) {
    return fetchJSON(lytta.routes.cartAdd, {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  },

  async change(id, quantity) {
    return fetchJSON(lytta.routes.cartChange, {
      method: 'POST',
      body: JSON.stringify({ id, quantity })
    });
  },

  async get() {
    return fetchJSON(lytta.routes.cart + '.js');
  }
};

/* ============================================================
   CART DRAWER WEB COMPONENT
   ============================================================ */
class LyttaCartDrawer extends HTMLElement {
  connectedCallback() {
    // AbortController scopes every global listener registered below to
    // this element's lifecycle. When Shopify swaps the section in the
    // theme editor (settings change → re-render), disconnectedCallback
    // aborts and the old listeners detach automatically — no zombie
    // handlers stack up across edits.
    this._ac = new AbortController();
    const signal = this._ac.signal;

    this.drawer = this.querySelector('[data-drawer]');
    this.closeBtn = this.querySelector('[data-close-drawer]');
    this.itemsContainer = this.querySelector('[data-cart-items]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.countPillEl = this.querySelector('[data-cart-count-pill]');
    this.countTextEl = this.querySelector('[data-cart-count-text]');
    this.emptyEl = this.querySelector('[data-cart-empty]');
    this.contentEl = this.querySelectorAll('[data-cart-content]');
    this.shippingBar = this.querySelector('[data-shipping-bar]');
    this.shippingMessage = this.querySelector('[data-shipping-message]');
    this.shippingFill = this.querySelector('[data-shipping-fill]');
    this.upsell = this.querySelector('[data-cart-upsell]');
    this.note = this.querySelector('[data-cart-note]');
    this.noteCounter = this.querySelector('[data-note-counter]');
    this.discountInput = this.querySelector('[data-discount-input]');
    this.discountApply = this.querySelector('[data-discount-apply]');
    this.discountMessage = this.querySelector('[data-discount-message]');
    this.discountAppliedPill = this.querySelector('[data-discount-applied-pill]');
    this.checkoutLink = this.querySelector('[data-checkout-link]');
    this.termsCheckbox = this.querySelector('[data-terms-checkbox]');
    this._removeFocusTrap = null;
    this._removeStyle = (lytta.settings?.cartRemoveStyle) || 'icon';

    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    }, { signal });

    document.addEventListener('lytta:cart:open', () => this.open(), { signal });
    document.addEventListener('lytta:cart:refresh', () => this.refresh(), { signal });

    this._initNote();
    this._initDiscount();
    this._initUpsellHandlers();
    this._initTerms();
  }

  disconnectedCallback() {
    this._ac?.abort();
  }

  isOpen() {
    return this.drawer?.getAttribute('aria-hidden') === 'false';
  }

  async open() {
    await this.refresh();
    this.drawer?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    overlay.show(() => this.close());
    this._removeFocusTrap = trapFocus(this.drawer);
  }

  close() {
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    overlay.hide();
    this._removeFocusTrap?.();
  }

  async refresh() {
    const cartData = await cart.get();
    this.render(cartData);
  }

  render(cartData) {
    if (!this.itemsContainer) return;

    this._updateShippingBar(cartData);
    this._updateDiscountPill();

    if (cartData.item_count === 0) {
      this.emptyEl?.removeAttribute('hidden');
      this.contentEl?.forEach(el => el.setAttribute('hidden', ''));
      return;
    }

    this.emptyEl?.setAttribute('hidden', '');
    this.contentEl?.forEach(el => el.removeAttribute('hidden'));

    if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cartData.total_price);

    if (this.countPillEl && this.countTextEl) {
      this.countTextEl.textContent = cartData.item_count;
      this.countPillEl.removeAttribute('hidden');
    }

    if (this.note && cartData.note) this.note.value = cartData.note;
    this._updateNoteCounter();

    this.itemsContainer.innerHTML = cartData.items.map((item, i) => this.renderItem(item, i)).join('');

    this.itemsContainer.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.removeItem;
        const itemEl = btn.closest('.cart-item');
        if (itemEl) {
          itemEl.classList.add('is-leaving');
          await new Promise(r => setTimeout(r, 250));
        }
        await cart.change(key, 0);
        await this.refresh();
        document.dispatchEvent(new CustomEvent('lytta:cart:updated'));
      });
    });

    this.itemsContainer.querySelectorAll('[data-quantity-input]').forEach(input => {
      input.addEventListener('change', debounce(async (e) => {
        const key = e.target.dataset.quantityInput;
        const qty = parseInt(e.target.value, 10);
        await cart.change(key, qty);
        await this.refresh();
        document.dispatchEvent(new CustomEvent('lytta:cart:updated'));
      }, 400));
    });

    this.itemsContainer.querySelectorAll('[data-qty-minus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        if (!input) return;
        const current = parseInt(input.value, 10);
        const safe = Number.isFinite(current) ? current : 0;
        if (safe > 0) {
          input.value = safe - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    this.itemsContainer.querySelectorAll('[data-qty-plus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        if (!input) return;
        const current = parseInt(input.value, 10);
        const safe = Number.isFinite(current) ? current : 0;
        input.value = safe + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    this._updateUpsellVisibility(cartData);
  }

  _updateShippingBar(cartData) {
    if (!this.shippingBar || !lytta.settings?.freeShippingEnabled) return;

    // Spedizione sempre gratuita: non c'e' nessuna soglia da rincorrere e
    // nessuna barra da riempire. Il testo e' gia' nel markup, quindi qui
    // non si tocca niente: si esce e la riga resta visibile anche con il
    // carrello vuoto, dove e' un invito invece di un promemoria.
    if (lytta.settings.freeShippingMode === 'always') {
      this.shippingBar.removeAttribute('hidden');
      if (this.shippingMessage && !this.shippingMessage.innerHTML.trim()) {
        this.shippingMessage.innerHTML = (lytta.strings?.freeShippingAlways
          || 'Free shipping on <strong>every order</strong>');
      }
      return;
    }

    if (cartData.item_count === 0) {
      this.shippingBar.setAttribute('hidden', '');
      return;
    }

    const thresholdMajor = parseFloat(lytta.settings.freeShippingThreshold) || 50;
    const thresholdCents = thresholdMajor * 100;
    const subtotal = cartData.total_price;
    const remaining = Math.max(0, thresholdCents - subtotal);
    const progress = Math.min(100, (subtotal / thresholdCents) * 100);

    this.shippingBar.removeAttribute('hidden');
    if (this.shippingFill) this.shippingFill.style.setProperty('--progress', progress + '%');

    if (remaining <= 0) {
      this.shippingBar.setAttribute('data-reached', 'true');
      this.shippingMessage.innerHTML = (lytta.strings?.freeShippingUnlocked
        || "✓ You've unlocked <strong>free shipping</strong>!");
    } else {
      this.shippingBar.removeAttribute('data-reached');
      const amountFormatted = formatMoney(remaining);
      const template = lytta.strings?.freeShippingRemaining
        || 'Add <strong>{{ amount }}</strong> more for free shipping';
      this.shippingMessage.innerHTML = template.replace('{{ amount }}', amountFormatted);
    }
  }

  renderItem(item, index) {
    const brand = item.vendor || '';
    const variantTitle = item.variant_title && !item.product_has_only_default_variant ? item.variant_title : '';
    const hasSale = item.original_line_price && item.original_line_price > item.line_price;
    const safeKey = escapeHtml(item.key);
    const safeUrl = escapeHtml(item.url);
    const safeImage = escapeHtml(item.image || '');
    const removeMarkup = this._removeStyle === 'text'
      ? `<button class="cart-item__remove cart-item__remove--text" data-remove-item="${safeKey}" aria-label="Remove ${escapeHtml(item.product_title)}">${escapeHtml(lytta.strings?.cartRemove || 'Remove')}</button>`
      : `<button class="cart-item__remove cart-item__remove--icon" data-remove-item="${safeKey}" aria-label="Remove ${escapeHtml(item.product_title)}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>`;

    return `
      <div class="cart-item" data-key="${safeKey}" style="animation-delay: ${index * 60}ms">
        <a href="${safeUrl}" class="cart-item__image">
          ${item.image ? `<img src="${safeImage}" alt="${escapeHtml(item.product_title)}" width="88" height="117" loading="lazy">` : ''}
        </a>
        <div class="cart-item__details">
          ${brand ? `<span class="cart-item__brand">${escapeHtml(brand)}</span>` : ''}
          <a href="${safeUrl}" class="cart-item__title">${escapeHtml(item.product_title)}</a>
          ${variantTitle ? `<span class="cart-item__variant">${escapeHtml(variantTitle)}</span>` : ''}
          <div class="cart-item__row">
            <div class="quantity-input">
              <button class="quantity-input__btn" data-qty-minus aria-label="Decrease quantity">−</button>
              <input
                type="number"
                class="quantity-input__field"
                value="${item.quantity}"
                min="0"
                data-quantity-input="${safeKey}"
                aria-label="Quantity"
              >
              <button class="quantity-input__btn" data-qty-plus aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-item__price">
              ${formatMoney(item.final_line_price)}
              ${hasSale ? `<span class="cart-item__price-was">${formatMoney(item.original_line_price)}</span>` : ''}
            </span>
          </div>
          ${removeMarkup}
        </div>
      </div>
    `;
  }

  /* ============== Note (textarea + counter + persist) ============== */
  _initNote() {
    if (!this.note) return;
    this.note.addEventListener('input', debounce(async (e) => {
      this._updateNoteCounter();
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: e.target.value })
        });
      } catch (err) { console.warn('[cart note]', err); }
    }, 500));
  }

  _updateNoteCounter() {
    if (!this.note || !this.noteCounter) return;
    const len = this.note.value.length;
    const max = parseInt(this.note.getAttribute('maxlength'), 10) || 280;
    this.noteCounter.textContent = `${len}/${max}`;
    this.noteCounter.toggleAttribute('data-near-limit', len > max * 0.9);
  }

  /* ============== Discount code (localStorage + URL append at checkout) ============== */
  _initDiscount() {
    if (!this.discountInput || !this.discountApply) return;

    const stored = localStorage.getItem('lytta:cart-discount');
    if (stored) {
      this.discountInput.value = stored;
      this._showDiscountPill(stored);
      this._appendDiscountToCheckout(stored);
    }

    this.discountApply.addEventListener('click', () => {
      const code = (this.discountInput.value || '').trim().toUpperCase();
      if (!code) return;
      // localStorage can throw `QuotaExceededError` in iOS Safari private
      // browsing mode and on storage-full devices. Swallow the error so
      // the discount still applies in-session via _appendDiscountToCheckout
      // — the user just won't have it remembered on next visit.
      try { localStorage.setItem('lytta:cart-discount', code); } catch (e) { /* private mode / quota */ }
      this._showDiscountPill(code);
      this._appendDiscountToCheckout(code);
      this._showDiscountMessage(
        (lytta.strings?.cartDiscountApplied || 'Code applied at checkout: {{ code }}').replace('{{ code }}', code),
        'success'
      );
    });

    this.discountInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.discountApply.click();
      }
    });
  }

  _showDiscountPill(code) {
    if (!this.discountAppliedPill) return;
    this.discountAppliedPill.textContent = code;
    this.discountAppliedPill.removeAttribute('hidden');
  }

  _updateDiscountPill() {
    const stored = localStorage.getItem('lytta:cart-discount');
    if (stored) this._showDiscountPill(stored);
  }

  _showDiscountMessage(text, state) {
    if (!this.discountMessage) return;
    this.discountMessage.textContent = text;
    this.discountMessage.setAttribute('data-state', state);
    this.discountMessage.removeAttribute('hidden');
  }

  _appendDiscountToCheckout(code) {
    if (!this.checkoutLink || !code) return;
    // Defensive fallback: if the Liquid `routes.checkout_url` rendered
    // empty (rare edge case in some Shopify contexts) the resulting
    // baseUrl would be '', and the final href would be `?discount=CODE`
    // — a RELATIVE url that reloads the current page on click instead
    // of opening checkout. Always anchor to the canonical /checkout path.
    const rawHref = this.checkoutLink.getAttribute('href') || '';
    let baseUrl = rawHref.split('?')[0];
    if (!baseUrl) baseUrl = '/checkout';
    this.checkoutLink.setAttribute('href', `${baseUrl}?discount=${encodeURIComponent(code)}`);
  }

  /* ============== Upsell ============== */
  _initUpsellHandlers() {
    if (!this.upsell) return;

    this.upsell.querySelectorAll('[data-upsell-add]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.upsellAdd;
        if (!variantId) return;
        btn.dataset.loading = 'true';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          const item = btn.closest('.cart-upsell__item');
          if (item) item.dataset.added = 'true';
          await this.refresh();
          document.dispatchEvent(new CustomEvent('lytta:cart:updated'));
          // After 1.5s, hide the added item
          setTimeout(() => {
            if (item) item.style.display = 'none';
          }, 1500);
        } catch (err) {
          console.warn('[cart upsell add]', err);
        } finally {
          delete btn.dataset.loading;
        }
      });
    });
  }

  _updateUpsellVisibility(cartData) {
    if (!this.upsell) return;
    const cartProductIds = new Set(cartData.items.map(i => String(i.product_id)));
    let visibleCount = 0;
    this.upsell.querySelectorAll('.cart-upsell__item').forEach(item => {
      const pid = item.dataset.upsellProductId;
      const inCart = cartProductIds.has(String(pid));
      item.style.display = inCart ? 'none' : '';
      if (!inCart) visibleCount++;
    });
    this.upsell.toggleAttribute('hidden', visibleCount === 0);
  }

  /* ============== Terms checkbox gating checkout ============== */
  _initTerms() {
    if (!this.termsCheckbox || !this.checkoutLink) return;
    const updateState = () => {
      this.checkoutLink.toggleAttribute('data-disabled', !this.termsCheckbox.checked);
    };
    this.termsCheckbox.addEventListener('change', updateState);
    updateState();
  }
}

function escapeHtml(str) {
  return String(str || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

customElements.define('lytta-cart-drawer', LyttaCartDrawer);

/* ============================================================
   QUICK SHOP MODAL (snippet quick-shop.liquid, global)
   ============================================================
   Listens for any click on `[data-quick-shop]` anywhere in the document
   (product cards in featured-collection, main-collection, search,
   recommendations, brand pages, etc) and fetches the product's
   `?view=quick-shop` template into the dialog body. Falls back to a
   "View product" link if the fetch fails. */
class LyttaQuickShop extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    const signal = this._ac.signal;

    this.modal = this.querySelector('dialog');
    this.body = this.querySelector('#QuickShopBody');
    this.closeBtn = this.querySelector('[data-close-modal]');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.modal?.addEventListener('click', (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    }, { signal });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-shop]');
      if (btn) {
        e.preventDefault();
        this.open(btn.dataset.quickShop);
      }
    }, { signal });

    // Variant featured image sync. The section's `{% javascript %}`
    // block can't do this because it isn't executed when the modal
    // injects the template via innerHTML (HTML spec — innerHTML
    // scripts are inert). So we live here in the persistent component.
    document.addEventListener('lytta:variant:changed', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;
      const featured = this.body?.querySelector('[data-quick-shop-featured]');
      if (!featured) return;

      // Try every shape Shopify might serialize the variant image as:
      //  - `featured_image` (legacy Image API): {id, src, alt, ...}
      //  - `featured_media` (modern Media API): {id, preview_image: {src}, alt}
      //  - `featured_image` may be just a URL string in older themes
      let newSrc = null;
      let newAlt = null;
      const fi = variant.featured_image;
      const fm = variant.featured_media;
      if (typeof fi === 'string' && fi) {
        newSrc = fi;
      } else if (fi && fi.src) {
        newSrc = fi.src;
        newAlt = fi.alt;
      } else if (fm && fm.preview_image && fm.preview_image.src) {
        newSrc = fm.preview_image.src;
        newAlt = fm.alt;
      }

      if (!newSrc) {
        console.warn('[QuickShop] variant has no resolvable image. variant:', variant);
        return;
      }

      // Normalise protocol-relative URLs (some Shopify outputs start with `//`).
      if (newSrc.startsWith('//')) newSrc = 'https:' + newSrc;

      featured.src = newSrc;
      if (newAlt) featured.alt = newAlt;

      // Highlight the matching thumb if one exists.
      const thumbs = this.body.querySelectorAll('[data-quick-shop-thumb]');
      thumbs.forEach(t => {
        const baseUrl = (t.dataset.imageUrl || '').split('?')[0];
        const isMatch = baseUrl && newSrc.indexOf(baseUrl) !== -1;
        t.classList.toggle('is-active', isMatch);
      });
    }, { signal });
  }

  disconnectedCallback() {
    this._ac?.abort();
  }

  async open(handle) {
    this.modal.showModal();
    this.body.innerHTML = `
      <div class="quick-shop-modal__loading">
        <div class="quick-shop-modal__loading-spinner"></div>
      </div>
    `;

    try {
      const response = await fetch(`/products/${handle}?view=quick-shop`, {
        headers: { 'Accept': 'text/html' }
      });

      if (!response.ok) throw new Error();
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.quick-shop-product');

      if (content) {
        this.body.innerHTML = '';
        this.body.appendChild(content);
        this._setupVariantImageSync();
      } else {
        this.renderFallback(handle);
      }
    } catch {
      this.renderFallback(handle);
    }
  }

  renderFallback(handle) {
    this.body.innerHTML = `
      <div class="quick-shop-product__details" style="padding: var(--space-9); text-align: center;">
        <p style="opacity: 0.5; margin-bottom: var(--space-4);">${escapeHtml(window.lytta?.strings?.quickShopUnavailable || 'Preview not available')}</p>
        <a href="/products/${encodeURIComponent(handle)}" class="btn btn--primary">${escapeHtml(window.lytta?.strings?.viewProduct || 'View product')}</a>
      </div>
    `;
  }

  /* Direct variant→image sync, called after modal content is injected.
     Doesn't rely on the `lytta:variant:changed` event (which sometimes
     fails to bubble from the variant selector inside the dialog). We
     listen to the form's hidden `[name="id"]` input — Shopify's standard
     variant ID input that LyttaVariantSelector updates synchronously
     on every pill click — and resolve the image from the product JSON
     embedded in `<script data-product-json>`. */
  _setupVariantImageSync() {
    const featured = this.body?.querySelector('[data-quick-shop-featured]');
    if (!featured) return;

    const jsonScript = this.body.querySelector('[data-product-json]');
    if (!jsonScript) return;

    let productData;
    try { productData = JSON.parse(jsonScript.textContent); }
    catch { return; }
    if (!productData || !Array.isArray(productData.variants)) return;

    const variantInput = this.body.querySelector('input[name="id"], select[name="id"]');
    if (!variantInput) return;

    const swap = () => {
      const variantId = parseInt(variantInput.value, 10);
      if (!variantId) return;
      const variant = productData.variants.find(v => v.id === variantId);
      if (!variant) return;

      // Resolve image src across every shape Shopify might use.
      let newSrc = null;
      let newAlt = null;
      const fi = variant.featured_image;
      const fm = variant.featured_media;
      if (typeof fi === 'string' && fi) {
        newSrc = fi;
      } else if (fi && fi.src) {
        newSrc = fi.src;
        newAlt = fi.alt;
      } else if (fm && fm.preview_image && fm.preview_image.src) {
        newSrc = fm.preview_image.src;
        newAlt = fm.alt;
      } else if (variant.image_id && Array.isArray(productData.images)) {
        // Fallback: lookup the image URL by the variant's image_id
        // inside `productData.images` (array of strings or objects
        // depending on Shopify serialisation).
        const match = productData.images.find(img =>
          (typeof img === 'object' && img.id === variant.image_id)
        );
        if (match && match.src) newSrc = match.src;
      }
      if (!newSrc) return;
      if (newSrc.startsWith('//')) newSrc = 'https:' + newSrc;
      featured.src = newSrc;
      if (newAlt) featured.alt = newAlt;

      const thumbs = this.body.querySelectorAll('[data-quick-shop-thumb]');
      thumbs.forEach(t => {
        const baseUrl = (t.dataset.imageUrl || '').split('?')[0];
        const isMatch = baseUrl && newSrc.indexOf(baseUrl) !== -1;
        t.classList.toggle('is-active', isMatch);
      });
    };

    variantInput.addEventListener('change', swap);
    // Also poll for direct value mutations (some variant selectors
    // assign `.value` without dispatching 'change' explicitly).
    let lastValue = variantInput.value;
    const interval = setInterval(() => {
      if (variantInput.value !== lastValue) {
        lastValue = variantInput.value;
        swap();
      }
    }, 250);
    // Stop polling when the modal closes.
    this.modal?.addEventListener('close', () => clearInterval(interval), { once: true });
  }

  close() {
    this.modal?.close();
  }
}
customElements.define('lytta-quick-shop', LyttaQuickShop);

/* ============================================================
   QUANTITY CAP (product page + quick-shop modal)
   ============================================================
   Listens for `lytta:variant:changed` events anywhere in the document
   and updates the `max` attribute on `[data-product-quantity]` inputs
   based on the new variant's inventory. Browsers respect `max` on
   stepUp() so the +/− buttons can't exceed the cap. Out-of-stock
   variants leave the cap off (the ATC button is disabled anyway).
   No tracking / oversell allowed → no cap.

   Also installs a global `input` listener that clamps any manual entry
   (typing a number too high) on every `[data-product-quantity]` input.
   Belt + suspenders so the user really can't go past the cap. */
/* Shopify's storefront variant JSON carries `inventory_management` but never
   `inventory_quantity` or `inventory_policy`. Reading them off the variant
   object yields undefined, which both handlers below used to treat as "not
   tracked" — stripping the `max` the server had rendered and downgrading the
   stock badge to a generic "in stock". main-product.liquid publishes the real
   figures in [data-product-inventory]; resolve them by variant id, and when
   they are missing leave the server-rendered markup alone rather than guess. */
function _lyttaInventoryFor(variant) {
  if (!variant) return null;
  // A collection page with the quick-shop modal open holds two maps at once
  // (the modal's and, on a product page, the page's own). Variant ids are
  // unique, so ask every map and take the one that knows this variant.
  const id = String(variant.id);
  for (const el of document.querySelectorAll('[data-product-inventory]')) {
    if (!el._lyttaParsed) {
      try { el._lyttaParsed = JSON.parse(el.textContent); } catch (err) { el._lyttaParsed = {}; }
    }
    const hit = el._lyttaParsed[id];
    if (hit) return hit;
  }
  return null;
}

function _lyttaApplyQuantityCap(input, variant) {
  if (!input || !variant) return;
  const inv = _lyttaInventoryFor(variant);
  if (!inv) return;
  if (!inv.tracked || inv.policy === 'continue') {
    input.removeAttribute('max');
    return;
  }
  const qty = Number(inv.qty);
  if (Number.isFinite(qty) && qty > 0) {
    input.max = String(qty);
    if (Number(input.value) > qty) {
      input.value = String(qty);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } else {
    input.removeAttribute('max');
  }
}

document.addEventListener('lytta:variant:changed', (e) => {
  const variant = e.detail?.variant;
  if (!variant) return;
  document.querySelectorAll('[data-product-quantity]').forEach(input => {
    _lyttaApplyQuantityCap(input, variant);
  });
});

/* The +/− buttons are capped natively by `max` + stepUp(). Manual entry is
   not: the product form carries `novalidate`, so the browser never runs
   constraint validation, and a typed or pasted quantity above `max` reaches
   the cart unchallenged (Shopify then silently trims it at checkout, so the
   customer pays for fewer units than the page let them ask for).
   One delegated listener on the document, filtered by selector so it costs a
   single matches() call per event, plus a capture-phase submit pass to catch
   paste and autofill before the theme's fetch reads the value. */
function _lyttaClampQuantity(input) {
  const max = Number(input.max);
  if (!Number.isFinite(max) || max <= 0) return;
  if (Number(input.value) > max) {
    input.value = String(max);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

document.addEventListener('input', (e) => {
  const input = e.target;
  if (input instanceof HTMLInputElement && input.matches('[data-product-quantity]')) {
    _lyttaClampQuantity(input);
  }
});

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;
  form.querySelectorAll('[data-product-quantity]').forEach(_lyttaClampQuantity);
}, true);

/* ============================================================
   STOCK INDICATOR SYNC (product page + quick-shop modal)
   ============================================================
   Recomputes the `.product-stock` state class + label when the merchant
   switches variants. Mirrors the server-side Liquid logic in
   main-product / quick-shop-product (in / low / backorder / out) but on
   the client. The element exposes its low_stock_threshold + translated
   labels via data-attrs so we don't need to hard-code anything. */
document.addEventListener('lytta:variant:changed', (e) => {
  const variant = e.detail?.variant;
  if (!variant) return;

  document.querySelectorAll('[data-product-stock]').forEach(el => {
    const threshold = Number(el.dataset.lowThreshold || 5);
    let state = 'out';
    let count = 0;

    const inv = _lyttaInventoryFor(variant);
    if (variant.available) {
      if (!inv || !inv.tracked) {
        state = 'in';
      } else if (inv.qty > threshold) {
        state = 'in';
      } else if (inv.qty > 0) {
        state = 'low';
        count = inv.qty;
      } else if (inv.policy === 'continue') {
        state = 'backorder';
      } else {
        state = 'in';
      }
    }

    // Update modifier class
    el.className = el.className
      .replace(/product-stock--(in|low|backorder|out)/g, '')
      .trim() + ' product-stock--' + state;

    // Update label
    const labelEl = el.querySelector('.product-stock__label');
    if (labelEl) {
      let label = '';
      if (state === 'in') label = el.dataset.labelIn || 'In stock';
      else if (state === 'low') label = (el.dataset.labelLowTemplate || 'Only __COUNT__ left').replace(/__COUNT__/g, count);
      else if (state === 'backorder') label = el.dataset.labelBackorder || 'Pre-order';
      else if (state === 'out') label = el.dataset.labelOut || 'Sold out';
      labelEl.textContent = label;
    }
  });
});

/* ============================================================
   WISHLIST (localStorage-only, device-bound)
   ============================================================
   Storage key: lytta:wishlist
   Format: { items: ["product-handle-1", "product-handle-2"], updatedAt: 123 }

   Public API on `window.lyttaWishlist`:
     .get() → Array<handle>
     .has(handle) → boolean
     .toggle(handle) → boolean (true = added, false = removed)
     .add(handle), .remove(handle), .clear()
     .count() → number

   Custom events (dispatched on document):
     'lytta:wishlist:updated' { detail: { items, action, handle } }
     'lytta:wishlist:toast' { detail: { message, type } }
   ============================================================ */
const lyttaWishlist = (function() {
  const KEY = 'lytta:wishlist';
  const MAX = 250;

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      return Array.isArray(data?.items) ? data.items : [];
    } catch (e) { return []; }
  }

  function write(items) {
    const data = { items: items.slice(0, MAX), updatedAt: Date.now() };
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) { console.warn('[wishlist] storage full', e); }
  }

  function has(handle) { return read().includes(handle); }

  function add(handle) {
    if (!handle) return false;
    const items = read();
    if (items.includes(handle)) return false;
    if (items.length >= MAX) {
      // Refuse silently-dropping the oldest. Notify user via toast.
      document.dispatchEvent(new CustomEvent('lytta:wishlist:toast', {
        detail: {
          message: window.lytta?.strings?.wishlistFull || 'Wishlist full (250 items max)',
          type: 'remove'
        }
      }));
      return false;
    }
    items.unshift(handle);
    write(items);
    dispatch('add', handle, items);
    return true;
  }

  function remove(handle) {
    if (!handle) return false;
    let items = read();
    if (!items.includes(handle)) return false;
    items = items.filter(h => h !== handle);
    write(items);
    dispatch('remove', handle, items);
    return true;
  }

  function toggle(handle) {
    if (has(handle)) { remove(handle); return false; }
    add(handle); return true;
  }

  function clear() {
    write([]);
    dispatch('clear', null, []);
  }

  function count() { return read().length; }

  function dispatch(action, handle, items) {
    document.dispatchEvent(new CustomEvent('lytta:wishlist:updated', {
      detail: { action, handle, items, count: items.length }
    }));
  }

  return { get: read, has, add, remove, toggle, clear, count };
})();
window.lyttaWishlist = lyttaWishlist;

/* ============================================================
   <wishlist-heart> button — toggle + sync state across instances
   ============================================================ */
if (!customElements.get('wishlist-heart-syncer')) {
  // Pseudo-component: bind once on document for ALL [data-wishlist-toggle]
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.wishlistHandle;
    const title = btn.dataset.wishlistTitle || '';
    if (!handle) return;
    const added = lyttaWishlist.toggle(handle);
    const message = added
      ? (window.lytta?.strings?.wishlistAdded || 'Saved').replace('{{ title }}', title)
      : (window.lytta?.strings?.wishlistRemoved || 'Removed').replace('{{ title }}', title);
    document.dispatchEvent(new CustomEvent('lytta:wishlist:toast', {
      detail: { message, type: added ? 'add' : 'remove' }
    }));
  });

  function syncHearts() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const active = lyttaWishlist.has(btn.dataset.wishlistHandle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active);
      const addLabel = btn.querySelector('[data-wishlist-add-label]');
      const remLabel = btn.querySelector('[data-wishlist-remove-label]');
      if (addLabel) addLabel.toggleAttribute('hidden', active);
      if (remLabel) remLabel.toggleAttribute('hidden', !active);
    });
  }

  function syncCounters() {
    const c = lyttaWishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = c;
      el.toggleAttribute('hidden', c === 0);
    });
  }

  document.addEventListener('lytta:wishlist:updated', () => {
    syncHearts();
    syncCounters();
  });

  // Initial sync on page load (after the DOM is ready)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { syncHearts(); syncCounters(); });
  } else {
    syncHearts();
    syncCounters();
  }
}

/* ============================================================
   <lytta-wishlist-toast> — bottom toast feedback
   ============================================================ */
if (!customElements.get('lytta-wishlist-toast')) {
  class LyttaWishlistToast extends HTMLElement {
    connectedCallback() {
      this.timer = null;
      document.addEventListener('lytta:wishlist:toast', (e) => this.show(e.detail));
    }
    show({ message, type }) {
      this.textContent = message;
      this.dataset.type = type || 'add';
      this.dataset.visible = 'true';
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.dataset.visible = 'false'; }, 2200);
    }
  }
  customElements.define('lytta-wishlist-toast', LyttaWishlistToast);
}

/* ============================================================
   <lytta-wishlist-page> — renders saved products on /pages/wishlist
   ============================================================ */
if (!customElements.get('lytta-wishlist-page')) {
  class LyttaWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.tpl = this.querySelector('[data-wishlist-card-tpl]');
      this.clearBtn = this.querySelector('[data-wishlist-clear]');
      this.addAllBtn = this.querySelector('[data-wishlist-add-all]');

      this.clearBtn?.addEventListener('click', () => {
        if (!confirm(window.lytta?.strings?.wishlistClearConfirm || 'Clear all saved items?')) return;
        lyttaWishlist.clear();
      });

      this.addAllBtn?.addEventListener('click', () => this._addAllToCart());

      document.addEventListener('lytta:wishlist:updated', () => this.render());
      this.render();
    }

    async render() {
      const handles = lyttaWishlist.get();
      if (handles.length === 0) {
        this.grid.innerHTML = '';
        this.empty?.removeAttribute('hidden');
        if (this.addAllBtn) this.addAllBtn.disabled = true;
        return;
      }
      this.empty?.setAttribute('hidden', '');
      if (this.addAllBtn) this.addAllBtn.disabled = false;

      const fetches = handles.map(h => fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null));
      const products = await Promise.all(fetches);

      this.grid.innerHTML = '';
      products.forEach((p, i) => {
        if (!p) {
          // Product unpublished or deleted — skip and remove from storage
          lyttaWishlist.remove(handles[i]);
          return;
        }
        this.grid.appendChild(this._renderCard(p));
      });
    }

    _renderCard(p) {
      const article = document.createElement('article');
      article.className = 'wishlist-page__card';
      article.dataset.wishlistCardHandle = p.handle;

      const variantId = p.variants?.find(v => v.available)?.id || p.variants?.[0]?.id;
      const image = p.featured_image || p.images?.[0] || '';
      const onSale = p.compare_at_price && p.compare_at_price > p.price;

      const safeHandle = escapeHtml(p.handle);
      const safeUrl = escapeHtml(p.url);
      const safeImage = escapeHtml(image);
      const safeVariantId = variantId ? escapeHtml(String(variantId)) : '';
      const canAddToCart = p.available && !!variantId;

      article.innerHTML = `
        <button type="button" class="wishlist-page__remove" data-wishlist-toggle data-wishlist-handle="${safeHandle}" data-wishlist-title="${escapeHtml(p.title)}" aria-label="${escapeHtml((window.lytta?.strings?.wishlistRemove || 'Remove'))}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <a href="${safeUrl}" class="wishlist-page__image">
          ${image ? `<img src="${safeImage}" alt="${escapeHtml(p.title)}" width="400" height="500" loading="lazy">` : ''}
        </a>
        <div class="wishlist-page__body">
          ${p.vendor ? `<span class="wishlist-page__vendor">${escapeHtml(p.vendor)}</span>` : ''}
          <a href="${safeUrl}" class="wishlist-page__title">${escapeHtml(p.title)}</a>
          <div class="wishlist-page__price">
            <span class="wishlist-page__price-now">${formatMoney(p.price)}</span>
            ${onSale ? `<s class="wishlist-page__price-was">${formatMoney(p.compare_at_price)}</s>` : ''}
          </div>
          <button type="button" class="wishlist-page__atc" data-wishlist-add-to-cart data-variant-id="${safeVariantId}" ${!canAddToCart ? 'disabled' : ''}>
            ${canAddToCart ? escapeHtml(window.lytta?.strings?.addToCart || 'Add to cart') : escapeHtml(window.lytta?.strings?.soldOut || 'Sold out')}
          </button>
        </div>
      `;

      article.querySelector('[data-wishlist-add-to-cart]')?.addEventListener('click', async (e) => {
        const id = e.target.dataset.variantId;
        if (!id) return;
        e.target.disabled = true;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id, quantity: 1 })
          });
          if (!res.ok) throw new Error('add failed');
          document.dispatchEvent(new CustomEvent('lytta:cart:open'));
          document.dispatchEvent(new CustomEvent('lytta:cart:updated'));
        } catch (err) {
          console.warn('[wishlist add]', err);
          document.dispatchEvent(new CustomEvent('lytta:wishlist:toast', {
            detail: { message: window.lytta?.strings?.cartAddError || 'Could not add to cart', type: 'remove' }
          }));
        }
        finally { e.target.disabled = false; }
      });

      return article;
    }

    async _addAllToCart() {
      const handles = lyttaWishlist.get();
      if (handles.length === 0) return;
      this.addAllBtn.disabled = true;
      const fetches = handles.map(h => fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null));
      const products = await Promise.all(fetches);
      const items = products
        .filter(p => p && p.available)
        .map(p => {
          const v = p.variants?.find(v => v.available);
          return v ? { id: v.id, quantity: 1 } : null;
        })
        .filter(Boolean);
      if (items.length === 0) {
        this.addAllBtn.disabled = false;
        return;
      }
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        document.dispatchEvent(new CustomEvent('lytta:cart:open'));
        document.dispatchEvent(new CustomEvent('lytta:cart:updated'));
      } catch (err) { console.warn('[wishlist add all]', err); }
      finally { this.addAllBtn.disabled = false; }
    }
  }
  customElements.define('lytta-wishlist-page', LyttaWishlistPage);
}

/* ============================================================
   HEADER — STICKY BEHAVIOR
   ============================================================ */
class LyttaHeader extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    const signal = this._ac.signal;

    this._lastScroll = 0;
    this._isSticky = this.dataset.sticky === 'true';
    this._isTransparent = this.dataset.transparent === 'true';

    if (this._isSticky || this._isTransparent) {
      window.addEventListener('scroll', debounce(() => this._handleScroll(), 50), { passive: true, signal });
    }

    this._initSpacer(signal);

    // Cart button
    this.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (lytta.settings?.cartType === 'drawer') {
          document.dispatchEvent(new CustomEvent('lytta:cart:open'));
        } else {
          window.location.href = lytta.routes.cart;
        }
      });
    });

    // Search button
    this.querySelectorAll('[data-open-search]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('lytta:search:open'));
      });
    });

    // Aggiorna contatore carrello
    document.addEventListener('lytta:cart:updated', () => this._updateCartCount(), { signal });
    this._updateCartCount();
  }

  _handleScroll() {
    const current = window.scrollY;
    if (current > 80) {
      this.classList.add('is-scrolled');
    } else {
      this.classList.remove('is-scrolled');
    }
    this._lastScroll = current;
  }

  _initSpacer(signal) {
    const wrapper = this.closest('[data-header-wrapper]');
    const spacer = document.querySelector('[data-header-spacer]');
    const isTransparent = this.dataset.transparent === 'true';

    // Always publish the live header height as a CSS var so other CSS
    // (e.g. transparent-header first-section offset) can react to it.
    if (isTransparent) document.body.classList.add('has-transparent-header');

    const targetWrapper = wrapper;
    if (!targetWrapper) return;

    const sync = () => {
      const h = targetWrapper.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
      if (spacer) spacer.style.height = `${h}px`;
    };

    sync();
    window.addEventListener('load', sync, { signal });
    window.addEventListener('resize', debounce(sync, 100), { passive: true, signal });

    if ('ResizeObserver' in window) {
      this._spacerRO = new ResizeObserver(sync);
      this._spacerRO.observe(targetWrapper);
    }
  }

  disconnectedCallback() {
    this._ac?.abort();
    this._spacerRO?.disconnect();
  }

  async _updateCartCount() {
    try {
      const data = await cart.get();
      this.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = data.item_count;
        el.hidden = data.item_count === 0;
      });
    } catch (e) {
      // silently fail
    }
  }
}
customElements.define('lytta-header', LyttaHeader);

/* ============================================================
   DRAWER MENU (MOBILE)
   ============================================================ */
class LyttaDrawerMenu extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    const signal = this._ac.signal;

    this.drawer = this.querySelector('[data-drawer]');
    this.toggleBtns = document.querySelectorAll('[data-open-menu]');
    this.closeBtn = this.querySelector('[data-close-drawer]');
    this._removeFocusTrap = null;

    this.toggleBtns.forEach(btn => btn.addEventListener('click', () => this.open()));
    this.closeBtn?.addEventListener('click', () => this.close());

    // Collapsible nav items
    this.querySelectorAll('[data-collapsible-trigger]').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const target = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!isOpen));
        target?.toggleAttribute('hidden');
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    }, { signal });

    // Swipe-left to close (native mobile pattern)
    this._initSwipeClose();
  }

  disconnectedCallback() {
    this._ac?.abort();
  }

  _initSwipeClose() {
    if (!this.drawer) return;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.drawer.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1 || !this.isOpen()) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });

    this.drawer.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      // Swipe left ≥ 60px, mostly horizontal → close
      if (dx < -60 && dy < 50) this.close();
    }, { passive: true });
  }

  isOpen() {
    return this.drawer?.getAttribute('aria-hidden') === 'false';
  }

  // `aria-hidden` sul pannello e `aria-expanded` sul tasto dicono due cose
  // diverse: il primo se il pannello e' nascosto, il secondo se il tasto ha
  // aperto qualcosa. Il cassetto teneva il primo e non il secondo, cosi' chi
  // usa uno screen reader sentiva "menu, chiuso" con il menu aperto davanti.
  _dichiara(aperto) {
    this.toggleBtns?.forEach(b => b.setAttribute('aria-expanded', String(aperto)));
  }

  open() {
    this.drawer?.setAttribute('aria-hidden', 'false');
    this._dichiara(true);
    document.body.style.overflow = 'hidden';
    overlay.show(() => this.close());
    this._removeFocusTrap = trapFocus(this.drawer);
  }

  close() {
    this.drawer?.setAttribute('aria-hidden', 'true');
    this._dichiara(false);
    document.body.style.overflow = '';
    overlay.hide();
    this._removeFocusTrap?.();
  }
}
customElements.define('lytta-drawer-menu', LyttaDrawerMenu);

/* ============================================================
   PRODUCT FORM — ADD TO CART
   ============================================================ */
class LyttaProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitBtn = this.querySelector('[type="submit"]');
    this.originalBtnText = this.submitBtn?.textContent;

    this.form?.addEventListener('submit', (e) => this._handleSubmit(e));
  }

  async _handleSubmit(e) {
    e.preventDefault();
    if (!this.submitBtn || this.submitBtn.disabled) return;

    const formData = new FormData(this.form);
    const id = formData.get('id');
    const rawQty = parseInt(formData.get('quantity') || '1', 10);
    const quantity = Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 1;

    this.submitBtn.disabled = true;
    this.submitBtn.textContent = '...';

    try {
      await cart.add([{ id, quantity }]);

      // Trigger flyer BEFORE opening cart drawer (so user can see it)
      const sourceImg = this._getFlyerSource();
      flyToCart(sourceImg);

      document.dispatchEvent(new CustomEvent('lytta:cart:updated'));

      if (lytta.settings?.cartType === 'drawer') {
        // Slight delay so the flyer lands before the drawer opens
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('lytta:cart:open'));
        }, lytta.settings?.atcFlyerEnabled ? 650 : 0);
      }

      this.submitBtn.textContent = '✓';
      setTimeout(() => {
        this.submitBtn.textContent = this.originalBtnText;
        this.submitBtn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('Add to cart failed:', err);
      this.submitBtn.textContent = this.originalBtnText;
      this.submitBtn.disabled = false;
    }
  }

  _getFlyerSource() {
    // Priority:
    // 1. Explicit [data-flyer-source] marker (any section can opt-in)
    // 2. Active gallery slide (main product page)
    // 3. Any product-gallery image
    // 4. Quick-shop modal gallery
    // 5. Fallback: first visible img inside the form's scope
    const scope = this.closest('[data-product-section]')
               || this.closest('.quick-shop-modal')
               || document;
    return scope.querySelector('[data-flyer-source]')
        || scope.querySelector('.product-gallery__slide.is-active .product-gallery__img')
        || scope.querySelector('.product-gallery__img')
        || scope.querySelector('.quick-shop-product__gallery img')
        || scope.querySelector('img');
  }
}
customElements.define('lytta-product-form', LyttaProductForm);

/* ============================================================
   VARIANT SELECTOR
   ============================================================ */
class LyttaVariantSelector extends HTMLElement {
  connectedCallback() {
    try {
      this.productData = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '{}');
    } catch (err) {
      console.warn('[variant-selector] Invalid product JSON', err);
      this.productData = { variants: [] };
    }
    this.selects = this.querySelectorAll('select[data-option]');
    this.radios = this.querySelectorAll('input[type="radio"][data-option]');

    this.selects.forEach(s => s.addEventListener('change', () => {
      this._updateOptionHeader(s);
      this._updateVariant();
    }));
    this.radios.forEach(r => r.addEventListener('change', () => {
      this._updateOptionHeader(r);
      this._updateVariant();
    }));

    this._updateVariant();
  }

  /**
   * Updates the "currently selected value" display in the option group
   * header (e.g. "280 ml" appearing next to the "— Formato" label).
   * Scoped to the option group containing the changed input.
   */
  _updateOptionHeader(input) {
    const group = input.closest('[data-option-group]');
    const display = group?.querySelector('[data-option-current]');
    if (display) display.textContent = input.value;
  }

  _getSelectedOptions() {
    const options = {};
    this.selects.forEach(s => { options[s.dataset.option] = s.value; });
    this.radios.forEach(r => { if (r.checked) options[r.dataset.option] = r.value; });
    return options;
  }

  _findVariant(options) {
    return this.productData.variants?.find(v =>
      v.options.every((opt, i) => opt === options[`option${i + 1}`])
    );
  }

  _updateVariant() {
    const options = this._getSelectedOptions();
    const variant = this._findVariant(options);

    // The variant selector is a SIBLING of <lytta-product-form>, not an
    // ancestor — `this.closest('lytta-product-form')` returned null and
    // the hidden `<input name="id">` never got updated, so the cart kept
    // adding the variant rendered server-side from the URL. Walk up to
    // the product section wrapper, then find the form going down.
    const scope = this.closest('[data-product-section]')
               || this.closest('.quick-shop-product')
               || document;
    const form = scope.querySelector('lytta-product-form form')
              || scope.querySelector('form#ProductForm')
              || scope.querySelector('form[action*="/cart/add"]');

    if (!variant) return;

    // Aggiorna ID variante nel form
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;

    // Aggiorna prezzo
    const priceEl = scope.querySelector?.('[data-product-price]');
    if (priceEl && variant.price) {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Aggiorna pulsante
    const submitBtn = form?.querySelector('[type="submit"]');
    if (submitBtn) {
      if (!variant.available) {
        submitBtn.disabled = true;
        submitBtn.textContent = lytta.strings?.soldOut;
      } else {
        submitBtn.disabled = false;
        submitBtn.textContent = lytta.strings?.addToCart;
      }
    }

    // Dispatch evento
    this.dispatchEvent(new CustomEvent('lytta:variant:changed', {
      bubbles: true,
      detail: { variant }
    }));
  }
}
customElements.define('lytta-variant-selector', LyttaVariantSelector);

/* ============================================================
   PREDICTIVE SEARCH
   ============================================================ */
class LyttaSearch extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    const signal = this._ac.signal;

    this.layout = this.dataset.layout || 'drawer_top';
    this.maxProducts = parseInt(this.dataset.maxProducts || '6', 10);
    this.showCollections = this.dataset.showCollections !== 'false';

    this.drawer = this.querySelector('[data-search-drawer]');
    this.form = this.querySelector('[data-search-form]');
    this.input = this.querySelector('[data-search-input]');
    this.clearBtn = this.querySelector('[data-search-clear]');
    this.closeBtn = this.querySelector('[data-close-search]');

    this.defaultEl = this.querySelector('[data-search-default]');
    this.loadingEl = this.querySelector('[data-search-loading]');
    this.emptyEl = this.querySelector('[data-search-empty]');
    this.emptyMessageEl = this.querySelector('[data-search-empty-message]');
    this.resultsEl = this.querySelector('[data-search-results]');

    this.productsSection = this.querySelector('[data-search-section-products]');
    this.productsList = this.querySelector('[data-search-products]');
    this.collectionsSection = this.querySelector('[data-search-section-collections]');
    this.collectionsList = this.querySelector('[data-search-collections]');

    this.productTemplate = this.querySelector('[data-search-product-template]');
    this.collectionTemplate = this.querySelector('[data-search-collection-template]');
    this.viewAllLink = this.querySelector('[data-search-view-all]');
    this.viewAllText = this.querySelector('[data-search-view-all-text]');

    this._removeFocusTrap = null;
    this._lastQuery = '';
    this._controller = null;

    // Open from header button
    document.addEventListener('lytta:search:open', () => this.open(), { signal });

    // Close triggers
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    }, { signal });

    // Close when clicking the drawer's empty area (outside the panel)
    this.drawer?.addEventListener('click', (e) => {
      if (e.target === this.drawer) this.close();
    });

    // Input events
    this.input?.addEventListener('input', debounce((e) => this._onInput(e.target.value), 250));
    this.clearBtn?.addEventListener('click', () => this._clear());

    // Submit → go to search page with query
    this.form?.addEventListener('submit', (e) => {
      const q = this.input?.value.trim();
      if (!q) e.preventDefault();
    });
  }

  disconnectedCallback() {
    this._ac?.abort();
    this._controller?.abort();
  }

  isOpen() {
    return this.drawer?.getAttribute('aria-hidden') === 'false';
  }

  open() {
    this.drawer?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    overlay.show(() => this.close());
    // slight delay to allow transition before focusing
    setTimeout(() => {
      this.input?.focus();
      this._removeFocusTrap = trapFocus(this.drawer);
    }, 50);
  }

  close() {
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    overlay.hide();
    this._removeFocusTrap?.();
    this._controller?.abort();
  }

  _clear() {
    if (this.input) this.input.value = '';
    this.clearBtn?.setAttribute('hidden', '');
    this._showState('default');
    this.input?.focus();
  }

  _showState(state) {
    // states: default | loading | empty | results
    this.defaultEl?.toggleAttribute('hidden', state !== 'default');
    this.loadingEl?.toggleAttribute('hidden', state !== 'loading');
    this.emptyEl?.toggleAttribute('hidden', state !== 'empty');
    this.resultsEl?.toggleAttribute('hidden', state !== 'results');
  }

  async _onInput(query) {
    const q = query.trim();

    // Toggle clear button
    if (q.length > 0) {
      this.clearBtn?.removeAttribute('hidden');
    } else {
      this.clearBtn?.setAttribute('hidden', '');
    }

    if (q.length === 0) {
      this._showState('default');
      return;
    }

    if (q.length < 2) return; // need at least 2 chars

    if (q === this._lastQuery) return;
    this._lastQuery = q;

    this._showState('loading');

    try {
      const data = await this._fetchSuggestions(q);
      this._render(q, data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Search failed:', err);
      this._showState('empty');
      if (this.emptyMessageEl) {
        this.emptyMessageEl.textContent = lytta.strings?.searchError || 'Something went wrong. Try again.';
      }
    }
  }

  async _fetchSuggestions(query) {
    this._controller?.abort();
    this._controller = new AbortController();

    // Hard 8s timeout fallback so a hung connection (proxy, app conflict
    // with Search & Discovery, network blip) cannot leave the spinner
    // stuck forever. We surface the timeout as an error → empty state
    // with the localised "something went wrong" message.
    const timeoutId = setTimeout(() => this._controller?.abort(), 8000);

    const resources = this.showCollections ? 'product,collection' : 'product';

    // Minimal parameter set — `q`, `resources[type]`, `resources[limit]`.
    // Removed the optional `resources[options][fields]` and
    // `unavailable_products` keys: with the Search & Discovery app
    // installed Shopify sometimes 422-rejects requests that include
    // them, which manifested as a forever-spinning predictive search.
    // The defaults Shopify returns are already what we render.
    const params = new URLSearchParams({
      'q': query,
      'resources[type]': resources,
      'resources[limit]': String(this.maxProducts)
    });

    // Always use the canonical legacy endpoint. `routes.predictive_search_url`
    // does not exist as a Liquid variable — resolves to '' and the `||`
    // fallback was carrying us. Hardcoded for clarity.
    const url = `/search/suggest.json?${params.toString()}`;

    try {
      const res = await fetch(url, {
        signal: this._controller.signal,
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  _render(query, data) {
    const resources = data.resources?.results || {};
    const products = resources.products || [];
    const collections = resources.collections || [];

    const hasResults = products.length > 0 || collections.length > 0;

    if (!hasResults) {
      this._showState('empty');
      if (this.emptyMessageEl) {
        const template = lytta.strings?.searchNoResults || 'No results found for "{{ terms }}"';
        this.emptyMessageEl.innerHTML = template.replace('{{ terms }}', this._escape(query));
      }
      return;
    }

    // Render products
    if (products.length > 0 && this.productsList && this.productTemplate) {
      this.productsList.innerHTML = '';
      products.forEach(p => this.productsList.appendChild(this._buildProduct(p)));
      this.productsSection?.removeAttribute('hidden');
    } else {
      this.productsSection?.setAttribute('hidden', '');
    }

    // Render collections
    if (this.showCollections && collections.length > 0 && this.collectionsList && this.collectionTemplate) {
      this.collectionsList.innerHTML = '';
      collections.forEach(c => this.collectionsList.appendChild(this._buildCollection(c)));
      this.collectionsSection?.removeAttribute('hidden');
    } else {
      this.collectionsSection?.setAttribute('hidden', '');
    }

    // View all link
    if (this.viewAllLink) {
      const searchUrl = new URL(lytta.routes.search || '/search', window.location.origin);
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('type', 'product');
      this.viewAllLink.href = searchUrl.toString();
    }
    if (this.viewAllText) {
      const template = lytta.strings?.searchViewAllFor || 'View all results for "{{ terms }}"';
      this.viewAllText.textContent = template.replace('{{ terms }}', query);
    }

    this._showState('results');
  }

  _buildProduct(p) {
    const node = this.productTemplate.content.cloneNode(true);
    const link = node.querySelector('[data-product-link]');
    const img = node.querySelector('[data-product-image]');
    const title = node.querySelector('[data-product-title]');
    const vendor = node.querySelector('[data-product-vendor]');
    const price = node.querySelector('[data-product-price]');

    if (link) link.href = p.url;
    if (img) {
      if (p.image) {
        img.src = p.image;
        img.alt = p.title || '';
      } else {
        img.remove();
      }
    }
    if (title) title.textContent = p.title;
    if (vendor && p.vendor) {
      vendor.textContent = p.vendor;
      vendor.removeAttribute('hidden');
    }
    if (price) {
      // Shopify predictive_search.json returns already-formatted strings
      if (p.price_min && p.price_max && p.price_min !== p.price_max) {
        price.textContent = `${p.price_min} – ${p.price_max}`;
      } else {
        price.textContent = p.price || p.price_min || '';
      }
    }
    return node;
  }

  _buildCollection(c) {
    const node = this.collectionTemplate.content.cloneNode(true);
    const link = node.querySelector('[data-collection-link]');
    const title = node.querySelector('[data-collection-title]');
    if (link) link.href = c.url;
    if (title) title.textContent = c.title;
    return node;
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
customElements.define('lytta-search', LyttaSearch);

/* ============================================================
   KEN BURNS (slow zoom/pan on images)
   ============================================================ */
function initKenBurns() {
  if (!lytta.settings?.kenBurnsEnabled) return;
  const html = document.documentElement;
  html.classList.add('has-ken-burns');
  const duration = parseInt(lytta.settings.kenBurnsDuration, 10) || 20;
  html.style.setProperty('--ken-burns-duration', `${duration}s`);
}

/* ============================================================
   ADD-TO-CART FLYER (product image flies to cart icon)
   ============================================================ */
function flyToCart(sourceImg) {
  if (!lytta.settings?.atcFlyerEnabled) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!sourceImg) return;

  const cartIcon = document.querySelector('[data-open-cart]');
  if (!cartIcon) return;

  const srcRect = sourceImg.getBoundingClientRect();
  const tgtRect = cartIcon.getBoundingClientRect();

  // Skip if source is offscreen (e.g. scrolled away in stacked layout)
  if (srcRect.width === 0 || srcRect.height === 0) {
    _bounceCart(cartIcon);
    return;
  }

  // Clone the image as a flyer element
  const clone = sourceImg.cloneNode(true);
  clone.className = 'lytta-flyer';
  clone.removeAttribute('srcset');
  clone.removeAttribute('sizes');
  clone.style.left = srcRect.left + 'px';
  clone.style.top = srcRect.top + 'px';
  clone.style.width = srcRect.width + 'px';
  clone.style.height = srcRect.height + 'px';
  document.body.appendChild(clone);

  // Force reflow so the initial styles are applied before animating
  void clone.offsetWidth;

  const tgtX = tgtRect.left + tgtRect.width / 2;
  const tgtY = tgtRect.top + tgtRect.height / 2;
  const srcX = srcRect.left + srcRect.width / 2;
  const srcY = srcRect.top + srcRect.height / 2;
  const deltaX = tgtX - srcX;
  const deltaY = tgtY - srcY;

  // Use Web Animations API for a smooth fly with scale-down and fade
  const anim = clone.animate(
    [
      { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
      { transform: `translate(${deltaX * 0.5}px, ${deltaY * 0.3 - 60}px) scale(0.6) rotate(8deg)`, opacity: 0.95, offset: 0.55 },
      { transform: `translate(${deltaX}px, ${deltaY}px) scale(0.15) rotate(0deg)`, opacity: 0, offset: 1 }
    ],
    { duration: 800, easing: 'cubic-bezier(0.55, 0.05, 0.32, 1)', fill: 'forwards' }
  );

  anim.onfinish = () => {
    clone.remove();
    _bounceCart(cartIcon);
  };
}

function _bounceCart(cartIcon) {
  if (!cartIcon) return;
  cartIcon.animate(
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.25)' },
      { transform: 'scale(0.92)' },
      { transform: 'scale(1)' }
    ],
    { duration: 450, easing: 'cubic-bezier(0.36, 0, 0.2, 1)' }
  );
}

/* ============================================================
   PARALLAX IMAGES
   ============================================================ */
function initParallaxImages() {
  if (!lytta.settings?.parallaxImagesEnabled) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const elements = Array.from(document.querySelectorAll('[data-lytta-parallax]'));
  if (elements.length === 0) return;

  // Map each element to its speed multiplier
  const items = elements.map(el => {
    const speed = el.dataset.lyttaParallax;
    const multiplier = speed === 'slow' ? 0.15
                     : speed === 'fast' ? 0.5
                     : 0.3; // medium / default / unknown
    // Il verso. Serve per far salire una foto e scendere quella accanto:
    // senza, tutto deriva dalla stessa parte e il movimento non si nota,
    // perche' e' la differenza fra i due a leggersi come profondita'.
    const direction = el.dataset.lyttaParallaxDir === 'down' ? -1 : 1;
    return { el, multiplier: multiplier * direction, visible: false };
  });

  // Only parallax elements currently in viewport for perf
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      const item = items.find(i => i.el === e.target);
      if (item) item.visible = e.isIntersecting;
    });
  }, { rootMargin: '20% 0px' });

  items.forEach(i => io.observe(i.el));

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const vh = window.innerHeight;
      items.forEach(item => {
        if (!item.visible) return;
        const rect = item.el.getBoundingClientRect();
        // Distance of element center from viewport center, normalized
        const offset = (rect.top + rect.height / 2 - vh / 2);
        const translate = offset * item.multiplier * -0.2;
        item.el.style.transform = `translate3d(0, ${translate.toFixed(1)}px, 0)`;
      });
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll(); // initial position
}

/* ============================================================
   COUNTER ANIMATIONS
   ============================================================ */
function initCounters() {
  if (!lytta.settings?.counterAnimationsEnabled) return;

  const elements = document.querySelectorAll('[data-lytta-counter]');
  if (elements.length === 0) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const io = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      observer.unobserve(el);

      // Target can be set explicitly via data-lytta-counter="1000"
      // or parsed from the element's initial text (keeping any suffix)
      const raw = el.dataset.lyttaCounter.trim();
      const original = el.textContent.trim();
      const targetStr = raw || original;
      const match = targetStr.match(/-?[\d,]+(\.\d+)?/);
      if (!match) return;

      const target = parseFloat(match[0].replace(/,/g, ''));
      if (isNaN(target)) return;

      // Preserve prefix/suffix around the number (e.g. "$1,200+" → prefix "$", suffix "+")
      const prefix = targetStr.slice(0, targetStr.indexOf(match[0]));
      const suffix = targetStr.slice(targetStr.indexOf(match[0]) + match[0].length);
      const decimals = (match[0].split('.')[1] || '').length;
      const useComma = match[0].includes(',');

      if (reduced) {
        el.textContent = _formatCounter(target, decimals, useComma, prefix, suffix);
        return;
      }

      const duration = parseInt(el.dataset.lyttaCounterDuration, 10) || 1600;
      const start = performance.now();

      const step = (now) => {
        const p = Math.min((now - start) / duration, 1);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        const value = target * eased;
        el.textContent = _formatCounter(value, decimals, useComma, prefix, suffix);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }, { threshold: 0.4 });

  elements.forEach(el => io.observe(el));
}

function _formatCounter(value, decimals, useComma, prefix, suffix) {
  const fixed = value.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const intFormatted = useComma ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : intPart;
  const num = decPart ? `${intFormatted}.${decPart}` : intFormatted;
  return `${prefix}${num}${suffix}`;
}

/* ============================================================
   CUSTOM CURSOR
   ============================================================ */
class LyttaCursor extends HTMLElement {
  connectedCallback() {
    // Skip on touch devices, reduced-motion, or if disabled in settings
    const isTouch = matchMedia('(pointer: coarse)').matches;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isTouch || reduced || !lytta.settings?.customCursor) {
      this.remove();
      return;
    }

    this.dot = this.querySelector('[data-cursor-dot]');
    this.ring = this.querySelector('[data-cursor-ring]');

    this._pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    this._ringPos = { ...this._pos };
    this._visible = false;
    this._raf = null;

    document.documentElement.classList.add('has-custom-cursor');

    this._onMove = (e) => {
      this._pos.x = e.clientX;
      this._pos.y = e.clientY;
      if (!this._visible) {
        this._visible = true;
        this.classList.add('is-visible');
      }
    };

    this._onLeave = () => {
      this._visible = false;
      this.classList.remove('is-visible');
    };

    // Track hover state on interactive elements → enlarge ring
    this._onOver = (e) => {
      if (e.target.closest('a, button, input, textarea, select, [role="button"], [data-magnetic]')) {
        this.classList.add('is-hovering');
      } else {
        this.classList.remove('is-hovering');
      }
    };

    window.addEventListener('mousemove', this._onMove, { passive: true });
    window.addEventListener('mouseleave', this._onLeave);
    document.addEventListener('mouseover', this._onOver);

    this._tick();
  }

  _tick() {
    // Dot: snap to cursor
    if (this.dot) {
      this.dot.style.transform = `translate3d(${this._pos.x}px, ${this._pos.y}px, 0) translate(-50%, -50%)`;
    }
    // Ring: follow with easing (lerp)
    this._ringPos.x += (this._pos.x - this._ringPos.x) * 0.18;
    this._ringPos.y += (this._pos.y - this._ringPos.y) * 0.18;
    if (this.ring) {
      this.ring.style.transform = `translate3d(${this._ringPos.x}px, ${this._ringPos.y}px, 0) translate(-50%, -50%)`;
    }
    this._raf = requestAnimationFrame(() => this._tick());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._raf);
    window.removeEventListener('mousemove', this._onMove);
    window.removeEventListener('mouseleave', this._onLeave);
    document.removeEventListener('mouseover', this._onOver);
    document.documentElement.classList.remove('has-custom-cursor');
  }
}
customElements.define('lytta-cursor', LyttaCursor);

/* ============================================================
   MAGNETIC BUTTONS
   ============================================================ */
function initMagnetic() {
  if (!lytta.settings?.magneticEnabled) return;
  if (matchMedia('(pointer: coarse)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const strength = (lytta.settings?.magneticStrength ?? 30) / 100;

  // Auto-apply to main CTAs + icon buttons, plus anything explicitly opted-in
  const selector = [
    '[data-magnetic]',
    '.btn--primary',
    '.btn--secondary',
    '.header__icon-btn',
    '.header__menu-toggle'
  ].join(', ');

  document.querySelectorAll(selector).forEach(el => {
    // Avoid double-binding
    if (el.dataset.magneticBound === 'true') return;
    el.dataset.magneticBound = 'true';

    let rafId = null;
    let current = { x: 0, y: 0 };
    let target = { x: 0, y: 0 };

    const tick = () => {
      current.x += (target.x - current.x) * 0.2;
      current.y += (target.y - current.y) * 0.2;
      el.style.transform = `translate3d(${current.x}px, ${current.y}px, 0)`;
      if (Math.abs(current.x - target.x) > 0.1 || Math.abs(current.y - target.y) > 0.1) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };

    const start = () => { if (!rafId) rafId = requestAnimationFrame(tick); };

    el.addEventListener('mousemove', (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      target.x = (e.clientX - cx) * strength;
      target.y = (e.clientY - cy) * strength;
      start();
    });

    el.addEventListener('mouseleave', () => {
      target.x = 0;
      target.y = 0;
      start();
    });
  });
}

/* ============================================================
   INIT
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  initAnimations();
  initSplitText();
  initMagnetic();
  initParallaxImages();
  initCounters();
  initKenBurns();
  initAnnouncementMobile();
});

/* ============================================================
   ANNOUNCEMENT BAR — mobile rotate + marquee init
   ============================================================
   On mobile (≤768px) the merchant can pick a `mobile_behavior`:
     - rotate  → fade-cycle through messages every N seconds
     - marquee → continuous horizontal scroll (CSS-only, but we
                 duplicate the messages once so the loop seamlessly
                 wraps with `transform: translateX(-50%)` keyframe)
     - hide-extra / stack → CSS-only, no JS needed

   Idempotent: marks the bar with `data-mobile-init="true"` so that
   `shopify:section:load` reruns don't double-clone the marquee track. */
function initAnnouncementMobile() {
  const bars = document.querySelectorAll('.announcement-bar:not([data-mobile-init])');
  if (!bars.length) return;

  const mq = window.matchMedia('(max-width: 768px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  bars.forEach(bar => {
    bar.dataset.mobileInit = 'true';
    const behavior = bar.dataset.mobileBehavior;
    const inner = bar.querySelector('.announcement-bar__inner');
    if (!inner) return;

    // Marquee: duplicate the children once so that the CSS keyframe
    // translateX(-50%) lands exactly on the start of the duplicate set
    // → seamless infinite loop. We only do this on mobile-eligible bars
    // to avoid affecting desktop layout (mobile CSS hides the dupes via
    // inner width: max-content).
    if (behavior === 'marquee' && !reduced) {
      const originals = Array.from(inner.children);
      originals.forEach(node => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        // Dedicated marker so desktop CSS can hide ONLY these duplicates
        // and not e.g. decorative separators that are also aria-hidden.
        clone.setAttribute('data-marquee-clone', 'true');
        inner.appendChild(clone);
      });
    }

    // Rotate: cycle the `is-active` class on each direct child every
    // N seconds. Only one message visible at a time (CSS handles the
    // fade). Pauses while the bar is hovered/focused.
    if (behavior === 'rotate') {
      const items = Array.from(inner.children).filter(el =>
        !el.classList.contains('announcement-bar__sep')
      );
      if (items.length < 2) return; // single message, nothing to rotate

      // Active class only matters on mobile — we still set it always so
      // CSS can use it; desktop CSS overrides display anyway.
      let idx = 0;
      items.forEach((it, i) => it.classList.toggle('is-active', i === 0));

      const speed = (parseFloat(bar.dataset.rotateSpeed) || 5) * 1000;
      let paused = false;
      bar.addEventListener('mouseenter', () => paused = true);
      bar.addEventListener('mouseleave', () => paused = false);
      bar.addEventListener('focusin', () => paused = true);
      bar.addEventListener('focusout', () => paused = false);

      const advance = () => {
        if (!paused && mq.matches) {
          items[idx].classList.remove('is-active');
          idx = (idx + 1) % items.length;
          items[idx].classList.add('is-active');
        }
      };
      setInterval(advance, speed);
    }
  });
}

/* Re-run animation + split-text init when the theme editor reloads a
   section via Section Rendering API. Without this, freshly rendered
   [data-lytta-animate] elements stay stuck at opacity:0 (the new DOM
   is invisible to the IntersectionObserver attached on first page load).
   We scope to the loaded section element so only its descendants are
   processed. Both initAnimations and initSplitText are idempotent
   (skip already-visible / already-processed elements). */
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target;
  if (!section) return;
  initAnimations(section);
  // initSplitText is global (handles auto-tagging too) but its dataset
  // guard makes re-running safe.
  initSplitText();
  // Re-init announcement bar mobile rotate/marquee on section reload
  // (theme editor: when merchant changes the announcement settings,
  // Shopify rerenders the header section → new bar markup needs the
  // rotation interval / marquee clones bound).
  initAnnouncementMobile();
});
